import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { auth, db } from '@/integrations/firebase/config';
import { doc, getDoc } from 'firebase/firestore';

interface User {
  id: string;
  email: string;
  role: 'admin' | 'hod' | 'coordinator' | 'editor';
  name: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Resolve user role from Firestore `admins` collection, with fallback
async function resolveUserRole(firebaseUser: FirebaseUser): Promise<User | null> {
  try {
    // Try to get role from Firestore `admins` collection
    const adminDoc = await getDoc(doc(db, 'admins', firebaseUser.uid));
    if (adminDoc.exists()) {
      const data = adminDoc.data();
      return {
        id: firebaseUser.uid,
        email: firebaseUser.email || '',
        role: data.role || 'editor',
        name: data.name || firebaseUser.displayName || firebaseUser.email || 'Admin'
      };
    }

    // Fallback: any authenticated Firebase user gets 'admin' role
    // This ensures the admin panel works even without the Firestore admins collection
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      role: 'admin',
      name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Admin'
    };
  } catch (error) {
    console.error('Error resolving user role:', error);
    // If Firestore lookup fails, still allow login with default role
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      role: 'admin',
      name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Admin'
    };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen for Firebase auth state changes (persistent session)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const appUser = await resolveUserRole(firebaseUser);
        setUser(appUser);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log('Attempting login with email:', email);
      const credential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Login successful, user:', credential.user.uid);
      const appUser = await resolveUserRole(credential.user);
      setUser(appUser);
      return true;
    } catch (error: any) {
      console.error('Login error:', error.code, error.message);
      // Show actual error for debugging
      if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many login attempts. Please try again later.');
      }
      throw new Error(`Firebase error: ${error.code} — ${error.message}`);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      isAuthenticated: !!user,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}